// client/src/components/messages/RichTextEditor.js
import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  TextField,
  IconButton,
  Tooltip,
  ToggleButton,
  ToggleButtonGroup,
  Paper,
  Typography,
  Chip,
  CircularProgress
} from '@mui/material';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import CodeIcon from '@mui/icons-material/Code';
import LinkIcon from '@mui/icons-material/Link';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/Edit';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import CloseIcon from '@mui/icons-material/Close';
import axiosInstance from '../../axiosInstance';

const RichTextEditor = ({ value, onChange, placeholder, maxLength = 2000, disabled = false, threadId, attachments = [], onAttachmentsChange, socket }) => {
  const [viewMode, setViewMode] = useState('edit'); // 'edit' or 'preview'
  const [uploading, setUploading] = useState(false);
  const typingTimeoutRef = useRef(null);
  const isTypingRef = useRef(false);

  // Typing indicator logic (Phase 3: Week 2)
  const handleTyping = () => {
    if (!socket || !threadId) {
      console.log('⚠️ Typing: No socket or threadId', { socket: !!socket, threadId });
      return;
    }

    // Emit typing-start if not already typing
    if (!isTypingRef.current) {
      console.log('⌨️ Emitting typing-start for thread:', threadId);
      socket.emit('typing-start', { threadId });
      isTypingRef.current = true;
    }

    // Clear existing timeout
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }

    // Set timeout to emit typing-stop after 3 seconds of inactivity
    typingTimeoutRef.current = setTimeout(() => {
      if (socket && threadId) {
        console.log('⏹️ Emitting typing-stop for thread:', threadId);
        socket.emit('typing-stop', { threadId });
        isTypingRef.current = false;
      }
    }, 3000);
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
      if (socket && threadId && isTypingRef.current) {
        socket.emit('typing-stop', { threadId });
      }
    };
  }, [socket, threadId]);

  const insertMarkdown = (before, after = '') => {
    const textarea = document.getElementById('markdown-input');
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = value.substring(start, end);
    const newText = value.substring(0, start) + before + selectedText + after + value.substring(end);
    
    onChange({ target: { value: newText } });
    handleTyping(); // Trigger typing indicator
    
    // Restore cursor position
    setTimeout(() => {
      textarea.focus();
      const newCursorPos = start + before.length + selectedText.length;
      textarea.setSelectionRange(newCursorPos, newCursorPos);
    }, 0);
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (file.size > 10 * 1024 * 1024) {
      alert('File size must be less than 10MB');
      return;
    }

    try {
      setUploading(true);
      const formData = new FormData();
      formData.append('file', file);

      const response = await axiosInstance.post(`/messages/threads/${threadId}/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      onAttachmentsChange([...attachments, response.data]);
      event.target.value = ''; // Reset input
    } catch (err) {
      console.error('Upload error:', err);
      
      let errorMsg = 'Failed to upload file';
      if (err.response?.status === 413) {
        errorMsg = 'File too large. Maximum size is 10MB';
      } else if (err.response?.data?.error) {
        errorMsg = err.response.data.error;
      } else if (err.message) {
        errorMsg = err.message;
      }
      
      alert(`Upload failed: ${errorMsg}`);
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveAttachment = (index) => {
    onAttachmentsChange(attachments.filter((_, i) => i !== index));
  };

  const formatButtons = [
    { icon: <FormatBoldIcon />, tooltip: 'Bold', before: '**', after: '**' },
    { icon: <FormatItalicIcon />, tooltip: 'Italic', before: '*', after: '*' },
    { icon: <CodeIcon />, tooltip: 'Code', before: '`', after: '`' },
    { icon: <LinkIcon />, tooltip: 'Link', before: '[', after: '](url)' },
    { icon: <FormatListBulletedIcon />, tooltip: 'Bullet List', before: '- ', after: '' },
    { icon: <FormatQuoteIcon />, tooltip: 'Quote', before: '> ', after: '' },
  ];

  const renderMarkdown = (text) => {
    if (!text) return '';
    
    let html = text;
    
    // Bold
    html = html.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
    // Italic
    html = html.replace(/\*(.*?)\*/g, '<em>$1</em>');
    // Code
    html = html.replace(/`(.*?)`/g, '<code style="background: #f5f5f5; padding: 2px 4px; border-radius: 3px;">$1</code>');
    // Links
    html = html.replace(/\[(.*?)\]\((.*?)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer" style="color: #1976d2;">$1</a>');
    // Line breaks
    html = html.replace(/\n/g, '<br/>');
    
    return html;
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', gap: 0.5, mb: 1, flexWrap: 'wrap', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          {formatButtons.map((btn, idx) => (
            <Tooltip key={idx} title={btn.tooltip}>
              <IconButton
                size="small"
                onClick={() => insertMarkdown(btn.before, btn.after)}
                disabled={disabled || viewMode === 'preview'}
              >
                {btn.icon}
              </IconButton>
            </Tooltip>
          ))}
          
          <Tooltip title="Attach file (images, PDFs, text, ZIP - max 10MB)">
            <span>
              <input
                type="file"
                id="file-upload-input"
                style={{ display: 'none' }}
                onChange={handleFileUpload}
                disabled={disabled || uploading}
                accept="image/*,.pdf,.txt,.zip"
              />
              <IconButton
                size="small"
                component="label"
                htmlFor="file-upload-input"
                disabled={disabled || uploading}
              >
                {uploading ? <CircularProgress size={20} /> : <AttachFileIcon />}
              </IconButton>
            </span>
          </Tooltip>
        </Box>
        
        <Box sx={{ flexGrow: 1 }} />
        
        <ToggleButtonGroup
          value={viewMode}
          exclusive
          onChange={(e, newMode) => newMode && setViewMode(newMode)}
          size="small"
        >
          <ToggleButton value="edit">
            <EditIcon fontSize="small" sx={{ mr: 0.5 }} />
            Edit
          </ToggleButton>
          <ToggleButton value="preview">
            <VisibilityIcon fontSize="small" sx={{ mr: 0.5 }} />
            Preview
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {attachments.length > 0 && (
        <Box sx={{ display: 'flex', gap: 1, mb: 1, flexWrap: 'wrap' }}>
          {attachments.map((att, idx) => (
            <Chip
              key={idx}
              label={`${att.originalName} (${(att.size / 1024).toFixed(1)}KB)`}
              onDelete={() => handleRemoveAttachment(idx)}
              deleteIcon={<CloseIcon />}
              size="small"
              color="primary"
              variant="outlined"
            />
          ))}
        </Box>
      )}

      {viewMode === 'edit' ? (
        <TextField
          id="markdown-input"
          fullWidth
          multiline
          rows={3}
          placeholder={placeholder}
          value={value}
          onChange={(e) => {
            onChange(e);
            handleTyping(); // Trigger typing indicator on every keystroke
          }}
          disabled={disabled}
          inputProps={{ maxLength }}
        />
      ) : (
        <Paper 
          variant="outlined" 
          sx={{ 
            p: 2, 
            minHeight: 100, 
            backgroundColor: '#f9f9f9',
            '& code': { fontFamily: 'monospace' }
          }}
        >
          {value ? (
            <Typography
              component="div"
              dangerouslySetInnerHTML={{ __html: renderMarkdown(value) }}
              sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
            />
          ) : (
            <Typography color="text.secondary" fontStyle="italic">
              Nothing to preview
            </Typography>
          )}
        </Paper>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
        <Typography variant="caption" color="text.secondary">
          {value.length}/{maxLength}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          **bold** *italic* `code` [link](url) [game:Title] [lego:12345]
        </Typography>
      </Box>
    </Box>
  );
};

export default RichTextEditor;
